// @ts-nocheck
import * as THREE from "three";
import { unzip, strFromU8 } from "fflate";
import {
  createGenericBlockDefinition,
  shouldExposeBlock
} from "../core/MinecraftBlockCatalog.ts";
import type { BlockDefinition } from "../core/blockRegistry.ts";

const VERSION_MANIFEST_URLS = [
  "https://piston-meta.mojang.com/mc/game/version_manifest_v2.json",
  "https://launchermeta.mojang.com/mc/game/version_manifest_v2.json"
];
const RESOURCE_CDN_ROOT = "https://resources.download.minecraft.net";

function configureMinecraftTexture(texture) {
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestMipmapNearestFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
}

async function fetchJson(url) {
  const response = await fetch(url, { cache: "force-cache" });
  if (!response.ok) {
    throw new Error("HTTP " + response.status + " while loading " + url);
  }
  return response.json();
}

async function fetchFirstAvailableJson(urls) {
  let lastError = null;

  for (const url of urls) {
    try {
      return await fetchJson(url);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("No Minecraft metadata endpoint was available.");
}

async function fetchMinecraftImageBitmap(url) {
  const response = await fetch(url, { cache: "force-cache" });
  if (!response.ok) {
    throw new Error("HTTP " + response.status + " while loading texture.");
  }

  const blob = await response.blob();
  return createImageBitmap(blob);
}

function createTextureFromMinecraftBitmap(
  bitmap,
  useFirstAnimationFrame = false
) {
  const frameSize = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = useFirstAnimationFrame ? frameSize : bitmap.width;
  canvas.height = useFirstAnimationFrame ? frameSize : bitmap.height;

  const context = canvas.getContext("2d", { alpha: true });
  context.imageSmoothingEnabled = false;
  context.clearRect(0, 0, canvas.width, canvas.height);

  if (useFirstAnimationFrame) {
    context.drawImage(
      bitmap,
      0,
      0,
      frameSize,
      frameSize,
      0,
      0,
      frameSize,
      frameSize
    );
  } else {
    context.drawImage(bitmap, 0, 0);
  }

  bitmap.close();

  return configureMinecraftTexture(new THREE.CanvasTexture(canvas));
}

export function normalizeResourceLocation(resourceLocation, defaultFolder) {
  const raw = String(resourceLocation || "");
  const separator = raw.indexOf(":");
  const namespace = separator >= 0 ? raw.slice(0, separator) : "minecraft";
  let path = separator >= 0 ? raw.slice(separator + 1) : raw;

  if (defaultFolder && !path.startsWith(defaultFolder + "/")) {
    path = defaultFolder + "/" + path;
  }

  return { namespace, path };
}

export function getClientTexturePath(texturePath) {
  const normalized = normalizeResourceLocation(
    String(texturePath || "").replace(/\.png$/i, "")
  );

  return (
    "assets/" +
    normalized.namespace +
    "/textures/" +
    normalized.path +
    ".png"
  );
}

export interface MinecraftReleaseVersion {
  id: string;
  type: string;
  releaseTime: string;
  url: string;
}

export async function fetchMinecraftReleaseVersions(): Promise<{
  latestRelease: string;
  versions: MinecraftReleaseVersion[];
}> {
  const manifest = await fetchFirstAvailableJson(VERSION_MANIFEST_URLS);
  const minimumModernRelease = Date.parse("2018-07-18T00:00:00Z");

  return {
    latestRelease: manifest.latest.release,
    versions: manifest.versions
      .filter((entry) => {
        return (
          entry.type === "release" &&
          Date.parse(entry.releaseTime) >= minimumModernRelease
        );
      })
      .map((entry) => ({
        id: entry.id,
        type: entry.type,
        releaseTime: entry.releaseTime,
        url: entry.url
      }))
  };
}

export class MinecraftOfficialAssetLoader {
  constructor(requestedVersionId: string | null = null) {
    this.requestedVersionId = requestedVersionId;
    this.assetIndex = null;
    this.versionMetadata = null;
    this.versionId = null;
    this.versionType = null;
    this.initializationPromise = null;
    this.modelLibraryPromise = null;
    this.clientArchive = null;
    this.texturePromiseCache = new Map();
    this.externalTexturePromiseCache = new Map();
    this.jsonCache = new Map();
    this.statusListener = null;
    this.lastStatus = {
      status: "idle",
      detail: "Official Minecraft assets not loaded yet",
      versionId: null
    };
  }

  setStatusListener(listener) {
    this.statusListener = listener;
    if (this.statusListener) {
      this.statusListener(this.lastStatus);
    }
  }

  reportStatus(status, detail) {
    this.lastStatus = {
      status,
      detail: detail || "",
      versionId: this.versionId
    };

    if (this.statusListener) {
      this.statusListener(this.lastStatus);
    }
  }

  async initialize() {
    if (this.assetIndex && this.versionMetadata) {
      return this;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.initializeInternal();
    return this.initializationPromise;
  }

  async initializeInternal() {
    this.reportStatus("loading", "Resolving latest Minecraft Java release");

    const manifest = await fetchFirstAvailableJson(VERSION_MANIFEST_URLS);
    const queryVersion =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("mcVersion")
        : null;
    const targetVersionId =
      this.requestedVersionId || queryVersion || manifest.latest.release;
    const versionEntry = manifest.versions.find(function (entry) {
      return entry.id === targetVersionId;
    });

    if (!versionEntry) {
      throw new Error(
        "Minecraft Java version " + targetVersionId + " was not found."
      );
    }

    this.versionId = versionEntry.id;
    this.versionType = versionEntry.type;

    this.reportStatus(
      "loading",
      "Loading official metadata for Java " + this.versionId
    );

    this.versionMetadata = await fetchJson(versionEntry.url);
    if (
      !this.versionMetadata.assetIndex ||
      !this.versionMetadata.assetIndex.url
    ) {
      throw new Error("The selected Minecraft version has no asset index.");
    }

    this.assetIndex = await fetchJson(this.versionMetadata.assetIndex.url);
    this.reportStatus(
      "ready",
      "Official Mojang textures · Java " + this.versionId
    );
    return this;
  }

  async initializeModelLibrary() {
    if (this.clientArchive) {
      return this;
    }

    if (this.modelLibraryPromise) {
      return this.modelLibraryPromise;
    }

    this.modelLibraryPromise = (async () => {
      await this.initialize();

      const clientDownload =
        this.versionMetadata.downloads &&
        this.versionMetadata.downloads.client;

      if (!clientDownload || !clientDownload.url) {
        throw new Error(
          "The selected Minecraft version has no downloadable client JAR."
        );
      }

      this.reportStatus(
        "loading",
        "Loading vanilla block models from Java " + this.versionId
      );

      const response = await fetch(clientDownload.url, {
        cache: "force-cache"
      });

      if (!response.ok) {
        throw new Error(
          "HTTP " + response.status + " while loading Minecraft client JAR."
        );
      }

      const archiveBytes = new Uint8Array(await response.arrayBuffer());
      this.clientArchive = await new Promise(function (resolve, reject) {
        unzip(
          archiveBytes,
          {
            filter: function (file) {
              return (
                file.name.startsWith("assets/minecraft/blockstates/") ||
                file.name.startsWith("assets/minecraft/models/block/") ||
                file.name.startsWith("assets/minecraft/textures/")
              );
            }
          },
          function (error, files) {
            if (error) {
              reject(error);
              return;
            }
            resolve(files);
          }
        );
      });

      const requiredAssets = [
        "assets/minecraft/blockstates/stone_bricks.json",
        "assets/minecraft/models/block/stone_bricks.json",
        "assets/minecraft/textures/block/stone_bricks.png"
      ];

      const missingAssets = requiredAssets.filter(
        (assetPath) => !this.clientArchive[assetPath]
      );

      if (missingAssets.length > 0) {
        throw new Error(
          "Minecraft client JAR is missing expected vanilla assets: " +
            missingAssets.join(", ")
        );
      }

      this.reportStatus(
        "ready",
        "Official textures + block models verified · Java " + this.versionId
      );
      return this;
    })();

    try {
      return await this.modelLibraryPromise;
    } catch (error) {
      this.modelLibraryPromise = null;
      this.reportStatus(
        "error",
        "Vanilla block models unavailable · cube fallback active"
      );
      throw error;
    }
  }

  getAssetRecord(texturePath) {
    if (!this.assetIndex) {
      throw new Error("Minecraft asset loader is not initialized.");
    }

    const normalizedPath =
      "minecraft/textures/" +
      texturePath.replace(/^minecraft:/, "").replace(/\.png$/i, "") +
      ".png";

    const record = this.assetIndex.objects[normalizedPath];
    if (!record || !record.hash) {
      throw new Error("Minecraft texture was not found: " + normalizedPath);
    }

    return record;
  }

  getAssetUrl(texturePath) {
    const record = this.getAssetRecord(texturePath);
    return (
      RESOURCE_CDN_ROOT +
      "/" +
      record.hash.slice(0, 2) +
      "/" +
      record.hash
    );
  }

  async loadTexture(texturePath) {
    const cacheKey = texturePath.replace(/\.png$/i, "");

    if (this.texturePromiseCache.has(cacheKey)) {
      return this.texturePromiseCache.get(cacheKey);
    }

    const promise = (async () => {
      await this.initializeModelLibrary();

      const normalized = normalizeResourceLocation(cacheKey);
      const jarPath = getClientTexturePath(cacheKey);
      const jarBytes =
        this.clientArchive && this.clientArchive[jarPath];

      if (jarBytes) {
        const blob = new Blob([jarBytes], {
          type: "image/png"
        });
        const bitmap = await createImageBitmap(blob);
        const animationMetadataPath = jarPath + ".mcmeta";
        const isAnimated = Boolean(
          this.clientArchive &&
            this.clientArchive[animationMetadataPath]
        );
        return createTextureFromMinecraftBitmap(bitmap, isAnimated);
      }

      await this.initialize();
      const bitmap = await fetchMinecraftImageBitmap(
        this.getAssetUrl(cacheKey)
      );
      return createTextureFromMinecraftBitmap(bitmap);
    })();

    this.texturePromiseCache.set(cacheKey, promise);

    try {
      return await promise;
    } catch (error) {
      this.texturePromiseCache.delete(cacheKey);
      throw error;
    }
  }

  async loadExternalTexture(url) {
    const cacheKey = String(url || "").trim();
    if (!cacheKey) {
      throw new Error("External texture URL is empty.");
    }

    if (this.externalTexturePromiseCache.has(cacheKey)) {
      return this.externalTexturePromiseCache.get(cacheKey);
    }

    const promise = (async () => {
      const response = await fetch(cacheKey, {
        cache: "force-cache",
        mode: "cors"
      });
      if (!response.ok) {
        throw new Error(
          "HTTP " + response.status + " while loading external texture."
        );
      }

      const bitmap = await createImageBitmap(await response.blob());
      const texture = createTextureFromMinecraftBitmap(bitmap, false);
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      texture.needsUpdate = true;
      return texture;
    })();

    this.externalTexturePromiseCache.set(cacheKey, promise);

    try {
      return await promise;
    } catch (error) {
      this.externalTexturePromiseCache.delete(cacheKey);
      throw error;
    }
  }

  async loadBlockState(blockId) {
    await this.initializeModelLibrary();
    const normalized = normalizeResourceLocation(blockId);
    const archivePath =
      "assets/" +
      normalized.namespace +
      "/blockstates/" +
      normalized.path +
      ".json";
    return this.loadArchiveJson(archivePath);
  }

  async loadBlockModel(resourceLocation) {
    await this.initializeModelLibrary();
    const normalized = normalizeResourceLocation(
      resourceLocation,
      "block"
    );
    const archivePath =
      "assets/" +
      normalized.namespace +
      "/models/" +
      normalized.path +
      ".json";
    return this.loadArchiveJson(archivePath);
  }

  hasTexture(texturePath) {
    if (!this.clientArchive) {
      return false;
    }
    return Boolean(this.clientArchive[getClientTexturePath(texturePath)]);
  }

  findTexturePath(candidates) {
    for (const candidate of candidates) {
      if (this.hasTexture(candidate)) {
        return candidate;
      }
    }
    return null;
  }

  async buildBlockCatalog(): Promise<BlockDefinition[]> {
    await this.initializeModelLibrary();

    const prefix = "assets/minecraft/blockstates/";
    const suffix = ".json";
    const definitions: BlockDefinition[] = [];

    Object.keys(this.clientArchive)
      .filter((archivePath) => {
        return archivePath.startsWith(prefix) && archivePath.endsWith(suffix);
      })
      .sort()
      .forEach((archivePath) => {
        const blockId = archivePath.slice(
          prefix.length,
          archivePath.length - suffix.length
        );

        if (!shouldExposeBlock(blockId)) {
          return;
        }

        const blockStateDefinition = this.loadArchiveJson(archivePath);
        definitions.push(
          createGenericBlockDefinition(
            blockId,
            this.versionId,
            blockStateDefinition
          )
        );
      });

    return definitions;
  }

  hasClientAsset(archivePath) {
    return Boolean(this.clientArchive && this.clientArchive[archivePath]);
  }

  loadArchiveJson(archivePath) {
    if (this.jsonCache.has(archivePath)) {
      return this.jsonCache.get(archivePath);
    }

    const bytes = this.clientArchive && this.clientArchive[archivePath];
    if (!bytes) {
      throw new Error("Minecraft client resource not found: " + archivePath);
    }

    const parsed = JSON.parse(strFromU8(bytes));
    this.jsonCache.set(archivePath, parsed);
    return parsed;
  }
}
